import React, { useMemo } from "react";

import { useIntl } from "react-intl";

import SubNavBar from "Components/SubNavBar";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import useUrls from "Hooks/useUrls";
import getUrl from "Libs/getUrl";
import { escapeRegexSpecialChars, isDg2 } from "Libs/utils";
import { selectCurrentUserIsInternal } from "Reducers/app/selectors";
import { environmentSelector } from "Reducers/environment";
import { selectHasEnterpriseDeployment } from "Reducers/environment/environment.selectors";
import {
  selectFeatureHttpMetricsEnabled,
  selectFeatureProfilingEnabled,
  selectFeatureUsageBasedBillingEnabled
} from "Reducers/featureFlags/featureFlags.selectors";
import { gitProjectSelector } from "Reducers/project/project";
import { subscriptionSelector } from "Reducers/subscription";
import useDisableHttpMetricsForNonUsageBilled from "src/organization/pages/project/environment/metrics/hooks/useDisableHttpMetricsForNonUsageBilled";
import { getDeploymentType } from "src/organization/pages/project/environment/metrics/hooks/useMetricsLinkData";
import useObservabilitySandboxAccess from "src/organization/pages/project/environment/observabilitySandbox/hooks/useObservabilitySandboxAccess";
import { usePluginApps } from "src/root/pages/pluginApps/usePluginApps";
import { useAppSelector } from "Store/hooks";

import * as S from "./styles";

import type { NavigationTabType } from "Components/SubNavBar/types";

type SubNavBarContainerProps = {
  currentPathName: string;
  environmentId?: string;
  organizationId: string;
  projectId: string;
};

const SubNavBarContainer = ({
  currentPathName,
  environmentId = "",
  organizationId,
  projectId
}: SubNavBarContainerProps) => {
  const intl = useIntl();
  const [pluginApps] = usePluginApps();

  const project = useSelectorWithUrlParams(gitProjectSelector);
  const enableHttpMetrics = useAppSelector(selectFeatureHttpMetricsEnabled);
  const environment = useSelectorWithUrlParams(environmentSelector);
  const enterpriseDeployment = useSelectorWithUrlParams(
    selectHasEnterpriseDeployment
  );

  const [
    environmentOverviewUrl,
    servicesUrl,
    backupsUrl,
    metricsUrl,
    httpTrafficUrl,
    httpTraffic2Url,
    profilingUrl,
    observabilitySandboxUrl,
    projectSettingsUrl,
    environmentSettingsUrl
  ] = useUrls(
    [
      "organization.project.environment",
      "organization.project.environment.services",
      "organization.project.environment.backups",
      "organization.project.environment.metrics",
      "organization.project.environment.httpTraffic",
      "organization.project.environment.httpTraffic2",
      "organization.project.environment.profiling",
      "organization.project.environment.observabilitySandbox",
      "organization.project.settings",
      "organization.project.environment.settings"
    ],
    { environmentId: environmentId, projectId, organizationId }
  );

  const metrics2Url = metricsUrl.replaceAll("/metrics", "/metrics2");

  const profilingEnabled = useAppSelector(selectFeatureProfilingEnabled);
  const isUsageBilling = useAppSelector(selectFeatureUsageBasedBillingEnabled);

  const subscription = useAppSelector(state => {
    return subscriptionSelector(state, {
      organizationId,
      projectId,
      id: project?.getSubscriptionId()
    });
  });
  const { enterprise_tag, support_tier } = subscription || {};

  const isEnterpriseTier = !["trial", "standard"].includes(support_tier ?? "");
  const isInternalUser = useAppSelector(selectCurrentUserIsInternal);
  const enableProfilingTab = isUsageBilling
    ? profilingEnabled
    : profilingEnabled && isEnterpriseTier && isInternalUser;

  const canAccessObsSandbox = useObservabilitySandboxAccess();

  const isDg2Project = isDg2(enterprise_tag, enterpriseDeployment);

  const isProjectSettingsPage = currentPathName.startsWith(projectSettingsUrl);

  const isEnvironmentSettingsPage = currentPathName.startsWith(
    environmentSettingsUrl
  );

  const metricsType = useAppSelector(getDeploymentType);

  const disableHttpMetricsForNonUsageBilled =
    useDisableHttpMetricsForNonUsageBilled();

  const tabs: NavigationTabType[] = useMemo(() => {
    return (
      [
        {
          // Project pages
          regex: `^/${organizationId}/${projectId}(?:/-/(?:app(?:/[^/]+)?|integrations/setup))?/?$`,
          tabs: Object.keys(pluginApps)
            .filter(appId =>
              pluginApps[appId].hooks?.some(
                hook =>
                  hook.route === "/:organizationId/:projectId/" &&
                  hook.type === "tab"
              )
            )
            .map(appId => {
              const appName = pluginApps[appId].name;
              const hook = pluginApps[appId].hooks?.find(
                h =>
                  h.route === "/:organizationId/:projectId/" && h.type === "tab"
              );
              const title = hook?.title || appName || appId;
              return {
                name: `plugin.${title}`,
                route: `/${organizationId}/${projectId}/-/app/${appId}`
              };
            })
        },
        {
          // Environment pages
          regex: `^/${organizationId}/${projectId}/${escapeRegexSpecialChars(
            environmentId
          )}`,
          tabs: [
            {
              name: "overview",
              route: environmentOverviewUrl
            },
            {
              name: "services",
              route: servicesUrl,
              testPath: pathName => pathName?.startsWith(servicesUrl)
            },
            {
              name: "backups",
              route: backupsUrl,
              permission: "#edit",
              testPath: pathName => pathName?.startsWith(backupsUrl)
            },
            {
              name: "metrics",
              route: metricsUrl,
              permission: (currentPathName, links) =>
                links["#metrics"] &&
                ["dedicated", "grid", "dedication"].includes(metricsType ?? "")
            },
            {
              name: "metrics2", //clean up locales after
              route: metrics2Url,
              permission: (currentPathName, links) =>
                links["#metrics"] &&
                ["dedicated", "grid", "dedication"].includes(
                  metricsType ?? ""
                ) &&
                canAccessObsSandbox
            },
            {
              name: "http_traffic",
              tooltip: disableHttpMetricsForNonUsageBilled
                ? intl.formatMessage({ id: "http_metrics_unavailable" })
                : null,
              disabled: disableHttpMetricsForNonUsageBilled,
              route: httpTrafficUrl,
              permission: (_, links) =>
                links["#metrics"] &&
                ["dedicated", "grid", "dedication"].includes(
                  metricsType ?? ""
                ) &&
                enableHttpMetrics
            },
            {
              name: "http_traffic2",
              route: httpTraffic2Url,
              permission: (_, links) =>
                canAccessObsSandbox &&
                links["#metrics"] &&
                ["dedicated", "grid", "dedication"].includes(
                  metricsType ?? ""
                ) &&
                enableHttpMetrics
            },
            {
              name: "profiling",
              route: profilingUrl,
              permission: () => enableProfilingTab
            },
            {
              name: "observability_sandbox",
              route: observabilitySandboxUrl,
              permission: () => canAccessObsSandbox
            }
          ]
            .filter(value =>
              isDg2Project ? (value.name === "backups" ? null : value) : value
            )
            .concat(
              Object.keys(pluginApps)
                .filter(appId =>
                  pluginApps[appId].hooks?.some(
                    hook =>
                      hook.route ===
                        "/:organizationId/:projectId/:environmentId/" &&
                      hook.type === "tab"
                  )
                )
                .map(appId => {
                  const appName = pluginApps[appId].name;
                  const hook = pluginApps[appId].hooks?.find(
                    h =>
                      h.route ===
                        "/:organizationId/:projectId/:environmentId/" &&
                      h.type === "tab"
                  );
                  const title = hook?.title || appName || appId;
                  return {
                    name: `plugin.${title}`,
                    route: getUrl({
                      key: "organization.project.environment.app",
                      props: {
                        organizationId,
                        projectId,
                        environmentId: environmentId || "",
                        appId
                      }
                    })
                  };
                })
            )
        }
      ].find(route =>
        new RegExp(route.regex).test(decodeURIComponent(currentPathName))
      )?.tabs || []
    );
  }, [
    organizationId,
    projectId,
    backupsUrl,
    environmentId,
    environmentOverviewUrl,
    servicesUrl,
    metricsUrl,
    disableHttpMetricsForNonUsageBilled,
    intl,
    httpTrafficUrl,
    httpTraffic2Url,
    profilingUrl,
    observabilitySandboxUrl,
    metricsType,
    enableHttpMetrics,
    enableProfilingTab,
    canAccessObsSandbox,
    isDg2Project,
    currentPathName,
    metrics2Url,
    pluginApps
  ]);

  // We don't need to show tabs anymore on the new settings page
  // But don't move this line above the hooks, or React will throw an error
  if (isProjectSettingsPage || isEnvironmentSettingsPage) return null;

  return (
    <S.Wrapper>
      <SubNavBar
        currentPathName={currentPathName}
        tabs={tabs}
        links={
          environmentId && environment?._links
            ? environment._links
            : project?._links
        }
        islightbackground
      />
    </S.Wrapper>
  );
};

export default SubNavBarContainer;
